import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { Col, Row } from 'react-flexbox-grid';
import Button from 'antd/lib/button';
import Progress from 'antd/lib/progress';
import Tag from 'antd/lib/tag';
import {
  formatDate,
  formatMonthsCount,
  formatMonthsQuantity,
  currencyFormatter,
  percentageFormatter,
} from '../../../common/utils/helpers';
import {
  allocationType,
  allocationTypeT,
  allocationStatusT,
  allocationStatus,
  allocationActions,
  allocationActionsT,
  accountRoles,
  contractType,
} from '../../../common/enums';
import { ModalWindow } from '../../../common/components';
import DocumentsPanel from '../../../features/dashboard/components/DocumentsPanel';
import { selectAccountId } from '../../auth/ducks';
import CalendarContainer from '../containers/CalendarContainer';

const getPayedProgress = ({ amountPaidBack, amountWithProfit }) =>
  Number((amountPaidBack / amountWithProfit).toFixed(2)) * 100;

const getPayedInstallments = ({ amountPaidBack, amountWithProfit, termMonths}) =>
  Number(amountPaidBack) === 0 ? 0 :
  Math.round(amountPaidBack / parseFloat(amountWithProfit / termMonths).toFixed(2))

const getInvestedProgress = ({ amount, amountInvested }) => amountInvested / amount * 100;

const getAmountLeft = ({ amountPaidBack, amountWithProfit }) => amountWithProfit - amountPaidBack;

const mapStateToProps = state => ({
  accountId: selectAccountId(state),
});

@translate()
@connect(mapStateToProps)
export default class AllocationItem extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    allocation: PropTypes.object.isRequired,
    itemOrder: PropTypes.number.isRequired,
    showSignAllocationModal: PropTypes.func.isRequired,
    showConfirmAllocationModal: PropTypes.func.isRequired,
    deleteAllocation: PropTypes.func,
    accountId: PropTypes.string.isRequired,
  };

  state = {
    activePanel: '',
    isModalVisible: false,
  };

  toggleDocuments = () => {
    this.setState({
      activePanel: this.state.activePanel === 'documents' ? '' : 'documents',
    });
  };

  toggleCalendar = () => {
    this.setState({
      activePanel: this.state.activePanel === 'calendar' ? '' : 'calendar',
    });
  };

  showSignModal = () => {
    this.props.showSignAllocationModal(this.props.allocation);
  };

  showConfirmModal = () => {
    this.props.showConfirmAllocationModal(this.props.allocation.id);
  };

  onDelete = () => {
    const { allocation, deleteAllocation, accountId } = this.props;

    deleteAllocation({ allocationId: allocation.id, accountId })
  }

  renderStatus() {
    const { allocation } = this.props;

    let status = <Tag>{allocationStatusT[allocation.status]}</Tag>;

    if (allocation.action) {
      switch (allocation.action) {
        case allocationActions.SIGN_CONTRACT:
          status = (
            <Button size="small" type="primary" ghost onClick={this.showSignModal}>
              {allocationActionsT[allocation.action]}
            </Button>
          );
          break;
        case allocationActions.ENTER_SMS_CODE_CONTRACT:
          status = (
            <Button size="small" type="primary" ghost onClick={this.showConfirmModal}>
              {allocationActionsT[allocation.action]}
            </Button>
          );
          break;
        default:
          status = <Tag>{allocationStatusT[allocationStatus[allocation.status]]}</Tag>;
      }
    }

    if (
      allocation.type === allocationType.AUTOMATIC &&
      allocation.status === allocationStatus.PENDING &&
      allocation.amountInvested > 0
    ) {
      status = <Tag>{allocationStatusT[allocationStatus.PARTIALLY_INVESTED]}</Tag>;
    }

    return (
      <Row end="sm">
        <Col xs>{status}</Col>
      </Row>
    );
  }

  renderAmountInvestedProgress() {
    const { t, allocation } = this.props;

    const investedProgress = getInvestedProgress(allocation);

    return (
      <div>
        <Row between="xs">
          <Col xs={12} className="progress-title">
            <p>{t('progres alokácie')}</p>
            <p>
              {currencyFormatter(allocation.amountInvested)} /{' '}
              {currencyFormatter(allocation.amount)}
            </p>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <Progress percent={investedProgress} showInfo={false} />
          </Col>
        </Row>
      </div>
    );
  }

  renderProgress() {
    const { t, allocation } = this.props;

    const payedProgress = getPayedProgress(allocation);
    const payedInstallments = getPayedInstallments(allocation);

    return (
      <div>
        <Row between="xs">
          <Col xs={12} className="progress-title">
            <p>{t('Progres splatenia')}</p>
            <p>
              {currencyFormatter(allocation.amountPaidBack)} /{' '}
              {currencyFormatter(allocation.amountWithProfit)}
            </p>
          </Col>
        </Row>
        <Row >
          <div style={{ flexGrow: 1, paddingLeft: '0.5rem'}}>
            <Progress percent={payedProgress} showInfo={false} />
          </div>
          {
            allocation.investments.length === 1 && (
              <div className="text-right" style={{ paddingLeft: '1rem', paddingRight: '0.5rem'}}>
                <p>{`${payedInstallments} / ${allocation.termMonths}`}</p>
              </div>
            )
          }
        </Row>
      </div>
    );
  }

  renderButtons() {
    const { t, allocation, deleteAllocation, accountId } = this.props;

    return (
      <Row between="sm" className="allocation-actions">
        {((allocation.status === allocationStatus.PENDING && allocation.amountInvested === '0') ||
          allocation.status === allocationStatus.WAITING_SIGNATURE ||
          allocation.status === allocationStatus.WAITING_CONFIRMATION ||
          allocation.status === allocationStatus.WAITING_INSURANCE_SIGNATURE ||
          allocation.status === allocationStatus.WAITING_INSURANCE_CONFIRMATION
          ) &&
          deleteAllocation && (
            <Col xs={12} sm={6}>
              <Button type="danger" ghost size="default" className="btn-block" onClick={() => this.setState({isModalVisible: true})}>
                {t('Zrušiť investíciu')}
              </Button>
            </Col>
          )}
        {(allocation.contracts.length !== 0 || allocation.allocationBought) && (
          <React.Fragment>
            {
              ((allocation.type === allocationType.MANUAL && allocation.status === allocationStatus.INVESTED)
            || (allocation.type === allocationType.AUTOMATIC && allocation.status === allocationStatus.INVESTED && allocation.investments.length === 1)) && (
                <Col xs={12} sm={6}>
                  <Button
                    type="danger"
                    size="default"
                    onClick={this.toggleCalendar}
                    className="btn-block"
                  >
                    {t('Splátkový kalendár')}
                  </Button>
                </Col>
              )
            }
          </React.Fragment>
        )}
        {(allocation.contracts.length !== 0 && (
          <React.Fragment>
            <Col xs={12} sm={6}>
              <Button
                type="danger"
                size="default"
                onClick={this.toggleDocuments}
                className="btn-block"
              >
                {t('Dokumenty')}
              </Button>
            </Col>
          </React.Fragment>
        )
        )}
      </Row>
    );
  }

  renderDocumentsPanel() {
    const { allocation } = this.props;
    const { activePanel } = this.state;

    if (activePanel !== 'documents' || allocation.contracts.length === 0) {
      return null;
    }

    return (
      <DocumentsPanel
        contracts={allocation.contracts}
        toggleDocuments={this.toggleDocuments}
        accountRole={accountRoles.CREDITOR}
      />
    );
  }

  renderCalendar() {
    const { allocation } = this.props;
    const { activePanel } = this.state;

    if (activePanel !== 'calendar') {
      return null;
    }

    return (
      <CalendarContainer allocationId={allocation.id} toggleCalendar={this.toggleCalendar}/>
    );
  }

  render() {
    const { t, allocation, itemOrder } = this.props;
    if (allocation.allocationBought){
      allocation.contracts = []
    }
    if (allocation.allocationSell){
      allocation.contracts = allocation.contracts.filter(contract => contract.type !== contractType.ALLOCATION_TRANSFER)
    }
    const amountLeft = getAmountLeft(allocation);
    allocation.amountPaidBack = Math.max(0, allocation.amountPaidBack).toFixed(2) // amountPaidBack prop sometimes has negative -0,00000758etc. so this ensures it is zero
    return (
      <div className="item-wrapper">
        <Row>
          <Col xs={12} sm={6}>
            <h2>
              #{itemOrder} {allocationTypeT[allocation.type]}
            </h2>
            {allocation.status === allocationStatus.DISPOSED && (
              <p>
                <strong>{t('ODPREDANÁ ZA')}:</strong>{' '}
                <strong>{currencyFormatter(allocation.disposeAmount)}</strong>
              </p>
            )}
            {allocation.allocationSell && (
              <p>
                <strong>{t('PREDANÁ ZA')}:</strong>{' '}
                <strong>{currencyFormatter(allocation.allocationBuyout)}</strong>
              </p>
            )}
            {allocation.allocationBought && (
              <p>
                <strong>{t('KÚPENÁ ZA')}:</strong>{' '}
                <strong>{currencyFormatter(allocation.allocationBuyout)}</strong>
              </p>
            )}
            <p>
              {t('výška alokácie')}: <strong>{currencyFormatter(allocation.amount)}</strong>
            </p>
            {allocation.type === allocationType.AUTOMATIC &&  allocation.investments.length !== 1 && (
              <p>
                {t('alokované prostriedky')}:{' '}
                <strong>{currencyFormatter(allocation.amountInvested)}</strong>
              </p>
            )}
            <p>
              {t('splatené prostriedky')}:{' '}
              <strong>{currencyFormatter(allocation.amountPaidBack)}</strong>
            </p>
            <p>
              {t('zostáva splatiť')}: <strong>{currencyFormatter(amountLeft)}</strong>
            </p>
            <p>
              {t('úrok')}: <strong>{percentageFormatter(allocation.interest)}</strong>
            </p>
            <p>
              {t('dátum aktivácie investície')}: <strong>{formatDate(allocation.createdAt)}</strong>
            </p>
            <p>
              {t('dĺžka investície')}:{' '}
              <strong>
                {formatMonthsCount(allocation.termMonths)}{' '}
                {formatMonthsQuantity(allocation.termMonths)}
              </strong>
            </p>
          </Col>
          <Col xs={12} sm={6} className="item-right">
            {this.renderStatus()}
            {allocation.type === allocationType.AUTOMATIC &&
              allocation.status === allocationStatus.PENDING &&
              this.renderAmountInvestedProgress()}
            {allocation.status === allocationStatus.INVESTED && !allocation.allocationSell && this.renderProgress()}
            {this.renderButtons()}
          </Col>
        </Row>
        {this.renderDocumentsPanel()}
        {this.renderCalendar()}
        {
          this.state.isModalVisible ?
          <ModalWindow
            hideModal={() => this.setState({isModalVisible: false})}
            message={t('Naozaj chcete zrušiť túto investíciu?')}
            onSubmit={() => this.onDelete()}
          /> : null 
        }
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/features/allocations/components/AllocationItem.js